import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import DashboardHero from '../../components/DashboardHero';
import UpcomingEvents from '../../components/UpcomingEvents';
import QuickActions from '../../components/QuickActions';
import useAuthStore from '../../store/authStore';
import { userService, enrollmentService, fetchAllPages } from '../../services/api';
import { Card, StatCard, Button, EmptyState, FullPageSpinner, RadialProgress, HorizontalBarChart, TrendChart } from '../../components/ui';
import { bucketByWeek } from '../../utils/chartHelpers';

function ParentDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [enrollmentTrend, setEnrollmentTrend] = useState([]);
  // Unscoped for a parent = every approved child's records combined — the
  // per-child breakdowns below filter these client-side by student id.
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [assignmentSubs, setAssignmentSubs] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  useEffect(() => {
    let mounted = true;
    userService.getDashboard().then((res) => {
      if (mounted) setData(res.data);
    }).catch(() => {}).finally(() => {
      if (mounted) setLoading(false);
    });
    enrollmentService.getUnreadNotificationsCount().then((res) => {
      if (mounted) setUnread(res.data.unread || 0);
    }).catch(() => {});
    enrollmentService.getEvents().then((res) => {
      if (mounted) setEvents(res.data.results || res.data || []);
    }).catch(() => {}).finally(() => {
      if (mounted) setEventsLoading(false);
    });
    fetchAllPages('/enrollments/enrollments/')
      .then((rows) => { if (mounted) setEnrollmentTrend(bucketByWeek(rows, 'created_at')); })
      .catch(() => setEnrollmentTrend([]));
    fetchAllPages('/quizzes/attempts/')
      .then((rows) => { if (mounted) setQuizAttempts(rows); })
      .catch(() => setQuizAttempts([]));
    fetchAllPages('/assignments/submissions/')
      .then((rows) => { if (mounted) setAssignmentSubs(rows); })
      .catch(() => setAssignmentSubs([]));
    fetchAllPages('/enrollments/attendance/')
      .then((rows) => { if (mounted) setAttendanceRecords(rows); })
      .catch(() => setAttendanceRecords([]));
    return () => { mounted = false; };
  }, []);

  if (loading || !user) return <FullPageSpinner />;

  const children = data?.children || [];
  const stats = data?.stats || {};
  const overallProgress = children.length
    ? Math.round(children.reduce((sum, c) => sum + (c.average_progress || 0), 0) / children.length)
    : null;

  const avgQuizScore = quizAttempts.length
    ? Math.round(quizAttempts.reduce((sum, a) => sum + Number(a.score || 0), 0) / quizAttempts.length)
    : null;
  const attendanceRate = attendanceRecords.length
    ? Math.round((attendanceRecords.filter((a) => a.status === 'present').length / attendanceRecords.length) * 100)
    : null;
  const gradedAssignments = assignmentSubs.filter((s) => s.status === 'graded').length;

  const quizScoreByChild = children.map((c) => {
    const childAttempts = quizAttempts.filter((a) => String(a.student) === String(c.id));
    const avg = childAttempts.length ? Math.round(childAttempts.reduce((sum, a) => sum + Number(a.score || 0), 0) / childAttempts.length) : 0;
    return { label: c.name, value: avg };
  });
  const attendanceRateByChild = children.map((c) => {
    const childRecords = attendanceRecords.filter((a) => String(a.student?.id) === String(c.id));
    const rate = childRecords.length ? Math.round((childRecords.filter((a) => a.status === 'present').length / childRecords.length) * 100) : 0;
    return { label: c.name, value: rate };
  });
  const assignmentsByChild = children.map((c) => ({
    label: c.name,
    value: assignmentSubs.filter((s) => String(s.student?.id) === String(c.id)).length,
  }));

  const quickActions = [
    {
      label: t('Link a Child'),
      color: 'primary',
      onClick: () => navigate('/parent/link-child'),
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>,
    },
    {
      label: t('View Calendar'),
      color: 'green',
      onClick: () => navigate('/calendar'),
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    },
    {
      label: t('Open Messages'),
      color: 'purple',
      onClick: () => navigate('/messages'),
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>,
    },
  ];

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Parent Dashboard')} />
        <main className="flex-1 overflow-y-auto p-6">
          <DashboardHero
            greeting={t('Welcome back', { name: user.first_name })}
            subtitle={t('Following {{count}} children', { count: children.length })}
            stat={overallProgress != null ? { label: t('Avg. Progress'), value: `${overallProgress}%` } : null}
          />

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <StatCard label={t('Linked Children')} value={stats.children_count ?? 0} color="blue" />
            <StatCard
              label={t('Pending Link Requests')}
              value={stats.pending_link_requests ?? 0}
              color="yellow"
              onClick={() => navigate('/parent/link-child')}
            />
            <StatCard label={t('Notifications')} value={unread} color="purple" onClick={() => navigate('/messages')} />
            <StatCard label={t('Avg. Quiz Score')} value={avgQuizScore != null ? `${avgQuizScore}%` : '—'} color="green" />
            <StatCard label={t('Attendance Rate')} value={attendanceRate != null ? `${attendanceRate}%` : '—'} color="red" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-display font-bold text-gray-800">{t('Your Children')}</h3>
                <Button onClick={() => navigate('/parent/link-child')} size="sm">+ {t('Link a Child')}</Button>
              </div>

              {children.length === 0 ? (
                <Card>
                  <EmptyState
                    title={t('No linked children yet')}
                    description={t("Request a link to your child's student account to see their progress and grades.")}
                    action={<Button onClick={() => navigate('/parent/link-child')}>+ {t('Link a Child')}</Button>}
                  />
                </Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {children.map((c) => (
                    <Card key={c.id} padded={false} className="cursor-pointer hover:shadow-card-hover transition p-4 rounded-2xl" onClick={() => navigate(`/parent/children/${c.id}`)}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold">
                          {c.name?.[0] || '?'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 text-sm truncate">{c.name}</p>
                          <p className="text-xs text-gray-500 truncate">{c.email}</p>
                        </div>
                        <RadialProgress value={c.average_progress} size={40} strokeWidth={4} color="#6739e0" trackColor="#ece8fe" />
                      </div>
                      <div className="mt-3 bg-gray-50 rounded-lg py-2 text-center">
                        <p className="text-lg font-display font-bold text-gray-800">{c.active_enrollments}</p>
                        <p className="text-[11px] text-gray-500">{t('Active Courses')}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <QuickActions actions={quickActions} />
              <UpcomingEvents events={events} loading={eventsLoading} />
            </div>
          </div>

          {children.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <div className="bg-white rounded-2xl shadow-card p-4">
                <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Progress by Child')}</h3>
                <HorizontalBarChart bars={children.map((c) => ({ label: c.name, value: c.average_progress }))} />
              </div>
              <div className="bg-white rounded-2xl shadow-card p-4">
                <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Active Courses by Child')}</h3>
                <HorizontalBarChart bars={children.map((c) => ({ label: c.name, value: c.active_enrollments }))} valueSuffix="" />
              </div>
              <div className="bg-white rounded-2xl shadow-card p-4">
                <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Avg. Quiz Score by Child')}</h3>
                <HorizontalBarChart bars={quizScoreByChild} emptyLabel={t('No items found', { what: t('Avg. Quiz Score by Child') })} />
              </div>
              <div className="bg-white rounded-2xl shadow-card p-4">
                <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Attendance Rate by Child')}</h3>
                <HorizontalBarChart bars={attendanceRateByChild} emptyLabel={t('No items found', { what: t('Attendance Rate by Child') })} />
              </div>
              <div className="bg-white rounded-2xl shadow-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-display font-bold text-gray-800">{t('Assignments Submitted by Child')}</h3>
                  <span className="text-xs text-gray-500">{t('{{graded}}/{{total}} graded', { graded: gradedAssignments, total: assignmentSubs.length })}</span>
                </div>
                <HorizontalBarChart bars={assignmentsByChild} valueSuffix="" emptyLabel={t('No items found', { what: t('Assignments Submitted by Child') })} />
              </div>
              <div className="bg-white rounded-2xl shadow-card p-4 lg:col-span-2">
                <h3 className="text-sm font-display font-bold text-gray-800 mb-3">{t('Enrollment Activity Over Time')}</h3>
                <TrendChart points={enrollmentTrend} color="#6739e0" valueSuffix="" emptyLabel={t('No items found', { what: t('Enrollment Activity Over Time') })} />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default ParentDashboard;
